// Full-document shell for stearns pages rendered entirely through React.
// reactivated's renderer injects the bundle CSS/JS + __PRELOADED_PROPS__, so we
// deliberately emit none of that here.
//
// Mutable reactivated props (not Immutable<>): PageSkeleton renders the in-tree
// SiteHeader, which forwards into the shared RichNavigation component -- both
// are typed with mutable reactivated props. This matches the established
// SiteHeader precedent. The deeply-readonly StreamField body is handled
// by the body renderer in UtilityPageTemplate.
import React from "react";
import { ErrorBoundary } from "tsi-common-react/src/components/ErrorBoundary/index";
import { FlashMessages } from "tsi-common-react/src/components/Message/index";
import { MetaTags } from "tsi-common-react/src/components/MetaTags/index";
import {
    initPromoCalloutMessages,
    useFocusFirstFormError,
    useLinkRewrites,
    useRichTextDecorators,
} from "tsi-common-react/src/utils/richTextDecorators";
import { t } from "ttag";

import { Context, templates } from "@reactivated";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { Template as FinancingModal } from "../../templates/FinancingModalTemplate";
import { Template as SessionKeepAlive } from "../../templates/SessionKeepAliveTemplate";
import { NavListSnippet } from "../NavListSnippet";
import { SiteFooter } from "../SiteFooter";
import { SiteHeader } from "../SiteHeader";

type IPageChrome = templates.UtilityPageTemplate["page_chrome"];
type IMessage = IPageChrome["messages"][number];
type ISidebarNav = templates.UtilityPageTemplate["sidebar_nav"];

// Server-rendered Django flash messages, rendered + dismissed by the shared
// FlashMessages component (which owns per-item dismissal). This wraps it with
// the stearns close icon and the no-JS fallback banner.
const Messages = ({
    messages,
}: {
    messages: readonly IMessage[];
}): React.JSX.Element => (
    <>
        {messages.length > 0 && (
            <FlashMessages
                closeIcon={
                    <svg
                        className=""
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 25 25"
                    >
                        <title>Close</title>
                        <path d="M12.245 15.5a7.49 7.49 0 0 0-5.855 2.813l-.78-.626a8.49 8.49 0 0 1 6.635-3.187 8.49 8.49 0 0 1 6.636 3.187l-.78.626a7.49 7.49 0 0 0-5.856-2.813m-2-6a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m5.5 1.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3" />
                        <path
                            fillRule="evenodd"
                            d="M24.51 12.255c0 6.768-5.487 12.255-12.255 12.255S0 19.023 0 12.255 5.487 0 12.255 0 24.51 5.487 24.51 12.255m-1 0c0 6.216-5.04 11.255-11.255 11.255S1 18.47 1 12.255 6.04 1 12.255 1 23.51 6.04 23.51 12.255"
                            clipRule="evenodd"
                        />
                    </svg>
                }
                messages={messages.map((message) => ({
                    tags: message.tags,
                    content: (
                        <p>
                            <span
                                className={`messages__item__category messages__item__category--${message.level_tag}`}
                            >
                                {message.category_label}
                            </span>{" "}
                            {message.is_html ? (
                                // Only messages explicitly mark_safe'd
                                // server-side render as HTML; plain
                                // messages are auto-escaped.
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: message.text,
                                    }}
                                />
                            ) : (
                                <span>{message.text}</span>
                            )}
                        </p>
                    ),
                }))}
            />
        )}
        <noscript
            dangerouslySetInnerHTML={{
                __html: `<aside class="messages l-full-width"><ul class="messages__list"><li class="messages__item error"><div class="messages__item__content l-capped-width"><p><span class="messages__item__category messages__item__category--error">${t`Error`}</span>${t`Please enable Javascript`}</p></div></li></ul></aside>`,
            }}
        />
    </>
);

const UnsupportedWarning = ({
    staticBase,
}: {
    staticBase: string;
}): React.JSX.Element => {
    const browsers = [
        {
            title: t`Get Google Chrome`,
            href: "http://www.google.com/chrome",
            img: "tsicommon_cms/img/chrome_32x32.png",
            label: t`Google Chrome`,
        },
        {
            title: t`Get Mozilla Firefox`,
            href: "http://www.firefox.com",
            img: "tsicommon_cms/img/firefox_32x32.png",
            label: t`Mozilla Firefox`,
        },
        {
            title: t`Get Apple Safari`,
            href: "http://www.apple.com/safari/download",
            img: "tsicommon_cms/img/safari_32x32.png",
            label: t`Apple Safari`,
        },
        {
            title: t`Get Microsoft Edge`,
            href: "https://www.microsoft.com/en-us/edge",
            img: "tsicommon_cms/img/edge_32x32.png",
            label: t`Microsoft Edge`,
        },
    ];
    return (
        <>
            <div className="unsupported-browser-container">
                <div className="unsupported-browser">
                    <a
                        className="unsupported-browser__close-btn"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            const container =
                                e.currentTarget.parentNode?.parentNode;
                            if (container instanceof HTMLElement) {
                                container.style.display = "none";
                            }
                        }}
                    >
                        {t`Close`}
                    </a>
                    <div className="unsupported-browser__title">
                        {t`Improve Your Shopping Experience`}
                    </div>
                    <p className="unsupported-browser__copy">
                        {t`You’re using an unsupported web browser.`}
                        <br />
                        {t`A list of the most popular browsers can be found below. Just click on an icon to visit the download page.`}
                    </p>
                    <ul className="unsupported-browser__list">
                        {browsers.map((b) => (
                            <li
                                className="unsupported-browser__list-item"
                                key={b.img}
                            >
                                <a
                                    title={b.title}
                                    href={b.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        src={`${staticBase}${b.img}`}
                                        alt={b.title}
                                        title={b.title}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <br />
                                    {b.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="unsupported-mobileos-container">
                <div className="unsupported-mobileos">
                    <a
                        className="unsupported-mobileos__close-btn"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            const container =
                                e.currentTarget.parentNode?.parentNode;
                            if (container instanceof HTMLElement) {
                                container.style.display = "none";
                            }
                        }}
                    >
                        {t`Close`}
                    </a>
                    <div className="unsupported-mobileos__title">
                        {t`Your Software is Out of Date`}
                    </div>
                    <p className="unsupported-mobileos__copy">
                        {t`Your phone or tablet is using software unsupported by our website. To improve your shopping experience, please update your device to the latest version of iOS or Android OS.`}
                    </p>
                </div>
            </div>
        </>
    );
};

// Per-page chrome (title, meta, canonical, JSON-LD, body_class, flash
// messages) is carried as the `page_chrome` field on each full-page
// @template. The global document shell (head/body globals, header, footer)
// comes from the `chrome` context processor (useContext below), not from these
// props.
type IProps = {
    pageChrome: IPageChrome;
    children?: React.ReactNode;
    // Header/footer slots. A node renders in place of the default
    // <SiteHeader>/<SiteFooter>; `false` omits the slot entirely; `undefined`
    // (default) renders the site chrome, so existing full-document pages are
    // unaffected.
    header?: React.ReactNode | false;
    footer?: React.ReactNode | false;
    // Messages slot. `false` suppresses the flash-messages aside (pair with
    // build_page_chrome's include_messages=False so the flash queue is not
    // drained either); `undefined` (default) renders pageChrome.messages.
    messages?: false;
    // Main-wrapper control for the embeddable nav/footer iframe documents.
    // `false` renders children directly with no <main id="main-content">
    // wrapper (the iframe fragments only carry the iframe-resizer scripts, which
    // the legacy templates emitted at the end of <body>, not inside <main>);
    // `undefined` (default) wraps children in <main>, so every full-document
    // page is unchanged.
    wrapMain?: boolean;
    // Page-content chrome rendered inside the document shell: the optional left
    // sidebar nav_list (page.sidebar_nav) and the offers count its offers_link
    // blocks show. Full-document pages without a sidebar omit both.
    sidebarNav?: ISidebarNav;
    sidebarOffersCount?: number;
};

export const PageSkeleton = (props: IProps): React.JSX.Element => {
    const {
        pageChrome,
        children,
        header,
        footer,
        messages,
        wrapMain,
        sidebarNav,
        sidebarOffersCount,
    } = props;
    // Global chrome (header/nav, footer, head/body globals) comes from the
    // `chrome` context processor. PageSkeleton is always the full-document root
    // inside the reactivated Provider, so the context is populated in both SSR
    // and client hydration. This read is intentionally unguarded: callers
    // rendering outside a chrome-carrying Provider (stories/tests) must supply
    // a chrome fixture in their Context value.
    const context = React.useContext(Context);
    const chrome = context.chrome;
    // Post-hydration DOM decorators shared by every full-document page: fill CMS
    // rich-text promo tokens, apply the link-rewrite ruleset, and focus the
    // first form error.
    useRichTextDecorators([initPromoCalloutMessages]);
    useLinkRewrites();
    useFocusFirstFormError();
    // On a full-document React page React owns <html className="no-js"> (the
    // no-JS fallback) and `data-useragent` drives the unsupported-browser CSS
    // (html[data-useragent*="MSIE 10.0"], etc.). Both are per-user / JS-gated and
    // these pages are cacheable, so neither can be server-rendered, and an early
    // imperative mutation would race hydration and mismatch on the React-owned
    // <html>. Apply both here, after hydration commits: React owns <html> but
    // never carries `data-useragent` in its vdom and won't re-render <html> (its
    // props don't change), so removing `no-js` sticks. index.tsx still drops
    // `no-js` eagerly on island pages (no full-document hydration to race);
    // legacy.js still sets `data-useragent` for old browsers that never boot
    // React (see legacy.tsx's module-support guard).
    React.useEffect(() => {
        const win = getWindow();
        const doc = getDocument();
        if (doc?.documentElement) {
            doc.documentElement.classList.remove("no-js");
            if (win?.navigator) {
                doc.documentElement.setAttribute(
                    "data-useragent",
                    win.navigator.userAgent,
                );
            }
        }
    }, []);
    // Page-level layout writers: dynamic CSS custom properties for the
    // configurator upsell modal offsets and the product-hero sticky offset, plus
    // the mobile sticky-header-on-scroll toggle. All read/write the live DOM, so
    // they run client-only after hydration (the properties fall back to their
    // SCSS defaults until then).
    const pageType = pageChrome.page_type;
    React.useEffect(() => {
        const win = getWindow();
        const doc = getDocument();
        if (!win || !doc) {
            return;
        }
        const root = doc.documentElement;
        const mainContent = doc.getElementById("main-content");
        // The upsell info modal panel tracks the top of #main-content (clamped
        // to 0 once scrolled past it); recomputed on every scroll.
        const setUpsellPanelOffset = () => {
            if (!mainContent) {
                return;
            }
            const top = mainContent.getBoundingClientRect().top;
            root.style.setProperty(
                "--configurator--upsell--panel-offset",
                top < 0 ? "0px" : `${top}px`,
            );
        };
        // One-shot product-hero sticky offset: the top of #main-content.
        if (mainContent) {
            root.style.setProperty(
                "--product-hero--sticky-offset",
                `${mainContent.getBoundingClientRect().top}px`,
            );
        }
        // The upsell modal overlay footer offset is the hardcoded service-footer
        // height, set only when the service footer is on the page.
        if (doc.querySelector(".service-footer-container")) {
            root.style.setProperty(
                "--configurator--upsell--panel-overlay-footer-offset",
                "50px",
            );
        }
        setUpsellPanelOffset();
        // Mobile sticky-header-on-scroll: add/remove the `sticky` class on the
        // header by scroll direction once scrolled past its height. Skipped on
        // the product-compare page, matching the legacy
        // body[data-track-page-type] gate.
        const header = doc.querySelector<HTMLElement>("header");
        const stickyNavEnabled = pageType !== "ProductComparePage";
        // Read the header height once: it's fixed for the effect's lifetime
        // (it only depends on `pageType`, which is in the dep array), so
        // re-reading `getBoundingClientRect()` per scroll event would force a
        // synchronous layout reflow on every frame.
        const headerHeight = header ? header.getBoundingClientRect().height : 0;
        let lastScrollTop = 0;
        const applyScroll = () => {
            setUpsellPanelOffset();
            if (stickyNavEnabled && header) {
                const st = win.scrollY;
                if (st > headerHeight && st > lastScrollTop) {
                    header.classList.add("sticky");
                } else {
                    header.classList.remove("sticky");
                }
                lastScrollTop = st <= 0 ? 0 : st;
            }
        };
        // Coalesce scroll events into one rAF (the handler only reads layout +
        // toggles a class, so it never needs to cancel scrolling -> passive).
        let ticking = false;
        const onScroll = () => {
            if (ticking) {
                return;
            }
            ticking = true;
            win.requestAnimationFrame(() => {
                applyScroll();
                ticking = false;
            });
        };
        win.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            win.removeEventListener("scroll", onScroll);
        };
    }, [pageType]);
    return (
        <html
            className="no-js"
            lang="en-us"
            data-design-version={chrome.design_version}
        >
            <head>
                {/*
                 * stearns.html uses an inline `html{visibility:hidden;opacity:0}`
                 * FOUC guard, relying on the bundle CSS overriding it. In a
                 * full-document React render React 19 hoists the bundle stylesheet
                 * above any inline <style>, which would invert that order and leave
                 * the page permanently hidden. The bundle <link> is render-blocking
                 * in <head> here, so there is no FOUC to guard against -- we
                 * intentionally omit the hide.
                 */}
                <title>{pageChrome.title}</title>
                <meta
                    httpEquiv="content-type"
                    content="text/html; charset=UTF-8"
                />
                <meta name="created" content={chrome.created} />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0"
                />
                <meta
                    name="google-site-verification"
                    content={chrome.google_site_verification}
                />
                {pageChrome.search_description && (
                    <meta
                        name="description"
                        content={pageChrome.search_description}
                    />
                )}
                {pageChrome.search_keywords && (
                    <meta
                        name="keywords"
                        content={pageChrome.search_keywords}
                    />
                )}
                {pageChrome.robots_noindex && (
                    <meta name="robots" content="noindex" />
                )}
                <link rel="canonical" href={pageChrome.canonical_url} />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:site" content="@TempurPedic" />
                <meta name="twitter:title" content={pageChrome.seo_heading} />
                <meta
                    name="twitter:description"
                    content={pageChrome.search_description}
                />
                <meta name="twitter:url" content={pageChrome.current_url} />
                <meta property="og:title" content={pageChrome.seo_heading} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={pageChrome.current_url} />
                <meta
                    property="og:description"
                    content={pageChrome.search_description}
                />
                <meta
                    property="og:site_name"
                    content="Stearns &amp; Foster&reg;"
                />
                {/* Per-page social-share images ({% block extra_meta %} on the
                    pages that set them); no tag is rendered when empty. */}
                {pageChrome.twitter_image && (
                    <meta
                        name="twitter:image"
                        content={pageChrome.twitter_image}
                    />
                )}
                {pageChrome.og_image && (
                    <meta property="og:image" content={pageChrome.og_image} />
                )}
                <link
                    rel="mask-icon"
                    href="safari-pinned-tab.svg"
                    color="#19274E"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="180x180"
                    href={chrome.apple_touch_icon_url}
                />
                <meta name="msapplication-TileColor" content="#ffffff" />
                <meta
                    name="msapplication-TileImage"
                    content={chrome.mstile_image_url}
                />
                {/* WebSite + page (breadcrumb/webpage) JSON-LD
                    ({% google_page_schema %}) is rendered at the top of <body> --
                    see the head comment above and the body comment. */}
                <link
                    rel="shortcut icon"
                    type="image/x-icon"
                    href={chrome.favicon_url}
                />
                {/* Per-page stylesheet bundles ({% include_css %} additions in
                    {% block style %} on the pages that set them); rendered after
                    the reactivated bundle CSS so they win the cascade, like the
                    legacy {{ block.super }} ordering. Empty on most pages, which
                    then emit no tag. */}
                {pageChrome.extra_stylesheets.map((href) => (
                    <link
                        key={href}
                        rel="stylesheet"
                        type="text/css"
                        href={href}
                    />
                ))}
                {chrome.aws_waf_url && (
                    <script defer={true} src={chrome.aws_waf_url} />
                )}
            </head>
            <body
                id={chrome.body_id}
                data-appslug={chrome.app_slug}
                data-version={chrome.version}
                data-environment={chrome.environment}
                data-sentry-dsn={chrome.sentry_dsn}
                className={pageChrome.body_class}
                data-track-page-type={pageChrome.page_type}
                data-view-name={chrome.view_name}
            >
                {/*
                 * The consent stub, the third-party tags and the JSON-LD render
                 * at the TOP of <body>, not in <head>. React 19 hoists the
                 * <meta> and async <script src> tags up anyway, while an inline
                 * <script> in the React-owned <head> gets paired with whatever a
                 * third party injects there before hydration finishes -- shifting
                 * the whole head by one. OptanonWrapper only has to exist before
                 * the async OneTrust SDK calls it, GTM events queue in dataLayer,
                 * and JSON-LD is crawled anywhere.
                 */}
                <script
                    type="text/javascript"
                    dangerouslySetInnerHTML={{
                        __html: "function OptanonWrapper() { }",
                    }}
                />
                <MetaTags
                    tags={context.meta_tags.meta_tags.value}
                    doNotTrack={chrome.do_not_track}
                />
                {pageChrome.json_ld.map((schema, i) => (
                    <script
                        key={i}
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{ __html: schema }}
                    />
                ))}
                {messages !== false && (
                    <Messages messages={pageChrome.messages} />
                )}
                {/* The entire site-header stylesheet (_header.scss) is scoped
                 * under `body > div > header` / `#default > div > header`, so
                 * without this intervening <div> the header renders unstyled
                 * (e.g. the skip link becomes visible and the nav collapses). */}
                {header === undefined ? (
                    <div>
                        <SiteHeader {...chrome.site_header} />
                    </div>
                ) : (
                    header
                )}

                {wrapMain !== false ? (
                    <main id="main-content">
                        {/* A <div>, not a second <main>: the document must
                            expose exactly one main landmark for screen-reader
                            landmark navigation. Element count/position is
                            load-bearing for the page CSS (`.utility-page`
                            targets `#main-content > div`). */}
                        <div>
                            {sidebarNav && (
                                <aside>
                                    <NavListSnippet
                                        navList={sidebarNav}
                                        offersCount={sidebarOffersCount ?? 0}
                                    />
                                </aside>
                            )}
                            {/* Body-scoped boundary: with the whole document
                                hydrated as one React tree, an uncaught client
                                render error in any page-body block would
                                otherwise unmount the entire document,
                                including the header/footer chrome. SSR is
                                unaffected (componentDidCatch is client-only;
                                server render errors still propagate). */}
                            <div>
                                <ErrorBoundary>{children}</ErrorBoundary>
                            </div>
                        </div>
                    </main>
                ) : (
                    // The nav/footer iframe fragments render their
                    // iframe-resizer scripts directly here, with no <main>
                    // wrapper (the legacy iframe templates left <main> empty and
                    // put these scripts at the end of <body>).
                    children
                )}

                {footer === undefined ? (
                    <SiteFooter
                        footer={chrome.footer}
                        staticBase={chrome.static_base}
                    />
                ) : (
                    footer
                )}

                {/* DNT-gated Google Customer Reviews badge. References
                 * onload=renderBadge but never defines it, so this is a no-op
                 * script load. */}
                {!chrome.do_not_track && (
                    <script
                        src="https://apis.google.com/js/platform.js?onload=renderBadge"
                        async
                        defer
                    />
                )}
                <UnsupportedWarning staticBase={chrome.static_base} />

                <SessionKeepAlive />
                <FinancingModal />

                <script
                    defer={true}
                    src={`${chrome.static_base}dist2/legacy.js`}
                />
                {/* Load the Zendesk chat widget only when global chat is
                    enabled (GlobalChrome.zendesk_chat_enabled =
                    ChatSettings.chat_enabled) AND the page opted in
                    (PageChrome.chat_widget_enabled) and a widget key is
                    configured. */}
                {chrome.zendesk_chat_enabled &&
                    pageChrome.chat_widget_enabled &&
                    chrome.zendesk_key && (
                        <script
                            id="ze-snippet"
                            src={`https://static.zdassets.com/ekr/snippet.js?key=${chrome.zendesk_key}`}
                        />
                    )}
            </body>
        </html>
    );
};
